import React, { useState, useCallback } from 'react';
import { Routes, Route, Link, useParams, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import SectionFooter from './SectionFooter';
import landingData from '../data/ai/ai-landing';
import conceptsData from '../data/ai/ai-concepts';
import llmsData from '../data/ai/ai-llms';
import toolsData from '../data/ai/ai-tools';
import aiBlogs from '../data/ai/ai-blogs';
import './AI.css';

// ─── Shared Components ───────────────────────────────────────────────

function AINav() {
  const location = useLocation();
  const path = location.pathname;

  const links = [
    { to: '/ai', label: 'Overview' },
    { to: '/ai/concepts', label: 'Core Concepts' },
    { to: '/ai/llms', label: 'LLMs' },
    { to: '/ai/tools', label: 'Tools & Apps' },
    { to: '/ai/blogs', label: 'Blogs' },
  ];

  const isActive = (to) => {
    if (to === '/ai') return path === '/ai';
    return path.startsWith(to);
  };

  return (
    <nav className="ai-nav">
      {links.map(({ to, label }) => (
        <Link key={to} to={to} className={isActive(to) ? 'active' : ''}>
          {label}
        </Link>
      ))}
    </nav>
  );
}

function References({ references }) {
  if (!references || references.length === 0) return null;
  return (
    <div className="ai-references">
      <h3>References</h3>
      <ol>
        {references.map((ref) => (
          <li key={ref.id} id={`ref-${ref.id}`}>
            {ref.url ? (
              <a href={ref.url} target="_blank" rel="noopener noreferrer">{ref.text}</a>
            ) : (
              ref.text
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function CitationLink({ id }) {
  return (
    <a className="ai-citation" href={`#ref-${id}`} title={`See reference ${id}`}>
      [{id}]
    </a>
  );
}

function TextWithCitations({ text }) {
  if (!text) return null;
  const parts = text.split(/(\[\d+\])/g);
  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/^\[(\d+)\]$/);
        if (match) {
          return <CitationLink key={i} id={parseInt(match[1], 10)} />;
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function ContentRenderer({ content }) {
  if (!content) return null;
  return (
    <>
      {content.map((item, i) => {
        if (typeof item === 'string') {
          return <p key={i}><TextWithCitations text={item} /></p>;
        }
        if (item.type === 'bullets') {
          return (
            <ul key={i}>
              {item.items.map((bullet, j) => (
                <li key={j}><TextWithCitations text={bullet} /></li>
              ))}
            </ul>
          );
        }
        return null;
      })}
    </>
  );
}

// ─── Image Modal ────────────────────────────────────────────────────

function ImageModal({ src, alt, onClose }) {
  const [scale, setScale] = useState(1);

  const handleZoomIn = (e) => { e.stopPropagation(); setScale((s) => Math.min(s + 0.5, 5)); };
  const handleZoomOut = (e) => { e.stopPropagation(); setScale((s) => Math.max(s - 0.5, 0.5)); };
  const handleReset = (e) => { e.stopPropagation(); setScale(1); };

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setScale((s) => {
      const delta = e.deltaY > 0 ? -0.2 : 0.2;
      return Math.min(Math.max(s + delta, 0.5), 5);
    });
  }, []);

  return (
    <div className="ai-image-modal-overlay" onClick={onClose}>
      <div className="ai-image-modal-controls">
        <button onClick={handleZoomOut} title="Zoom out">&#8722;</button>
        <button onClick={handleReset} title="Reset zoom">{Math.round(scale * 100)}%</button>
        <button onClick={handleZoomIn} title="Zoom in">&#43;</button>
        <button onClick={onClose} title="Close">&times;</button>
      </div>
      <div className="ai-image-modal-content" onWheel={handleWheel}>
        <img
          src={src}
          alt={alt}
          style={{ transform: `scale(${scale})` }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}

// ─── Landing Page ────────────────────────────────────────────────────

function AILanding() {
  return (
    <div className="ai-landing">
      <h1>{landingData.title}</h1>
      <p className="ai-subtitle">{landingData.subtitle}</p>

      <div className="ai-intro">
        {landingData.intro.map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>

      <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: '32px', marginBottom: '12px' }}>
        How to use this section
      </h2>
      <div className="ai-section-cards">
        {landingData.howToUse.map((item, i) => (
          <Link key={i} to={item.path} className="ai-section-card">
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </Link>
        ))}
        <Link to={landingData.promptSection.path} className="ai-section-card">
          <h3>{landingData.promptSection.title}</h3>
          <p>{landingData.promptSection.description}</p>
        </Link>
      </div>

      <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: '32px', marginBottom: '12px' }}>
        Core philosophy
      </h2>
      <div className="ai-philosophy">
        <ul>
          {landingData.philosophy.map((item, i) => (
            <li key={i}>
              {item.link ? (
                <Link to={item.link}>{item.text}</Link>
              ) : (
                item.text
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Concepts Page ───────────────────────────────────────────────────

function AIConcepts() {
  return (
    <div>
      <div className="ai-page-header">
        <h1>{conceptsData.title}</h1>
        <p>{conceptsData.subtitle}</p>
      </div>

      {conceptsData.sections.map((section) => (
        <div key={section.id} className="ai-content-section">
          <h2>{section.title}</h2>
          <ContentRenderer content={section.content} />
        </div>
      ))}

      <References references={conceptsData.references} />
    </div>
  );
}

// ─── LLMs Page ───────────────────────────────────────────────────────

function AILLMs() {
  return (
    <div>
      <div className="ai-page-header">
        <h1>{llmsData.title}</h1>
        <p>{llmsData.subtitle}</p>
      </div>

      {llmsData.sections.map((section) => (
        <div key={section.id} className="ai-content-section">
          <h2>{section.title}</h2>
          <ContentRenderer content={section.content} />
        </div>
      ))}

      <References references={llmsData.references} />
    </div>
  );
}

// ─── Tools Page ──────────────────────────────────────────────────────

function AITools() {
  return (
    <div>
      <div className="ai-page-header">
        <h1>{toolsData.title}</h1>
        <p>{toolsData.subtitle}</p>
      </div>

      {toolsData.sections.map((section) => (
        <div key={section.id} className="ai-content-section">
          <h2>{section.title}</h2>
          <ContentRenderer content={section.content} />
        </div>
      ))}

      {toolsData.dosAndDonts && (
        <div className="ai-dos-donts">
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid var(--border-light)' }}>
            Do's and Don'ts
          </h2>
          {toolsData.dosAndDonts.map((group) => (
            <div key={group.category}>
              <h3>{group.category}</h3>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '50%' }}>Do</th>
                    <th style={{ width: '50%' }}>Avoid</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((row, i) => (
                    <tr key={i}>
                      <td className="do-cell">{row.do}</td>
                      <td className="dont-cell">{row.dont}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <References references={toolsData.references} />
    </div>
  );
}

// ─── Blog Index ─────────────────────────────────────────────────────

function AIBlogIndex() {
  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div>
      <div className="ai-page-header">
        <h1>AI Blogs</h1>
        <p>Deep dives, explainers, and essays on artificial intelligence</p>
      </div>

      <div className="ai-blog-list">
        {aiBlogs.map((blog) => (
          <Link key={blog.slug} to={`/ai/blogs/${blog.slug}`} className="ai-blog-card">
            {blog.image && (
              <div className="ai-blog-card-image">
                <img src={blog.image} alt={blog.imageAlt || blog.title} />
              </div>
            )}
            <div className="ai-blog-card-content">
              <h3>{blog.title}</h3>
              <p>{blog.summary}</p>
              <span className="ai-blog-card-date">{formatDate(blog.date)}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── Blog Detail ────────────────────────────────────────────────────

function AIBlogDetail() {
  const { slug } = useParams();
  const blog = aiBlogs.find((b) => b.slug === slug);
  const [modalImage, setModalImage] = useState(null);

  if (!blog) {
    return (
      <div>
        <Link to="/ai/blogs" className="ai-back">&#8592; All blogs</Link>
        <p>Blog post not found.</p>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div>
      <Link to="/ai/blogs" className="ai-back">&#8592; All blogs</Link>

      <div className="ai-blog-detail">
        <h1>{blog.title}</h1>
        <span className="ai-blog-date">{formatDate(blog.date)}</span>

        {blog.sections.map((section) => (
          <div key={section.id} className={section.title ? 'ai-content-section' : 'ai-blog-intro-section'}>
            {section.title && <h2>{section.title}</h2>}
            <ContentRenderer content={section.content} />
            {section.showImage && blog.image && (
              <div
                className="ai-blog-image clickable"
                onClick={() => setModalImage({ src: blog.image, alt: blog.imageAlt || blog.title })}
              >
                <img src={blog.image} alt={blog.imageAlt || blog.title} />
                <span className="ai-blog-image-hint">Click to enlarge</span>
              </div>
            )}
          </div>
        ))}

        <References references={blog.references} />
      </div>

      {modalImage && (
        <ImageModal
          src={modalImage.src}
          alt={modalImage.alt}
          onClose={() => setModalImage(null)}
        />
      )}
    </div>
  );
}

// ─── Main AI Component ──────────────────────────────────────────────

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AI() {
  return (
    <div className="ai-container">
      <ScrollToTop />
      <div className="ai-theme-toggle">
        <ThemeToggle />
      </div>
      <AINav />
      <Routes>
        <Route path="/" element={<AILanding />} />
        <Route path="/concepts" element={<AIConcepts />} />
        <Route path="/llms" element={<AILLMs />} />
        <Route path="/tools" element={<AITools />} />
        <Route path="/blogs" element={<AIBlogIndex />} />
        <Route path="/blogs/:slug" element={<AIBlogDetail />} />
      </Routes>
      <SectionFooter />
    </div>
  );
}

export default AI;
